import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Coffee, ShoppingBag, ShoppingCart, Trash2, Info, Star, Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import {
  SPECIALITIES, ROASTS, ROAST_LIMIT, GRINDS, QUANTITIES, VARIANTS,
  unitPriceInr, basePriceInr, specialityName, variantName, SIZE_LABELS,
} from "@/data/coffeeCatalog";
import { getCart, addToCart, removeFromCart } from "@/lib/cart";

const cardCls = (active) =>
  `cursor-pointer bg-white border rounded-2xl transition-all ${
    active ? "border-[#89937C] ring-2 ring-[#89937C]/30" : "border-[#E5E2D9] hover:border-[#89937C]/60"
  }`;

const Stars = ({ value, size = 14, onSet }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        onClick={onSet ? () => onSet(i) : undefined}
        data-testid={onSet ? `star-${i}` : undefined}
        className={`${i <= Math.round(value) ? "text-[#C57B57] fill-[#C57B57]" : "text-[#C9CFC0]"} ${onSet ? "cursor-pointer hover:scale-110 transition-transform" : ""}`}
      />
    ))}
  </div>
);

function ReviewsBlock({ speciality, variant }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    axios
      .get(`${API_BASE}/coffee/reviews`, { params: { speciality, variant } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));

  useEffect(() => {
    setData(null);
    setShowForm(false);
    if (speciality && variant) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speciality, variant]);

  if (!variant) return null;

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/coffee/reviews`, {
        speciality,
        variant,
        rating,
        name: name.trim(),
        comment: comment.trim() || null,
      });
      toast.success("Thanks for your review!");
      setShowForm(false);
      setName("");
      setComment("");
      setRating(5);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-block" className="mt-4 bg-white border border-[#E5E2D9] rounded-2xl p-5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Stars value={data?.avg || 0} />
          <span className="text-sm text-[#5C665A]" data-testid="reviews-summary">
            {data?.count ? `${data.avg} · ${data.count} review${data.count > 1 ? "s" : ""}` : "No reviews yet"}
          </span>
        </div>
        {!showForm && (
          <button
            type="button"
            data-testid="write-review-btn"
            onClick={() => setShowForm(true)}
            className="text-xs font-medium text-[#AD6A48] hover:text-[#2D372B] transition-colors"
          >
            Write a review
          </button>
        )}
      </div>

      {showForm && (
        <div className="mt-4 space-y-3" data-testid="review-form">
          <Stars value={rating} size={22} onSet={setRating} />
          <Input
            data-testid="review-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="bg-[#F7F5F0] border-[#E5E2D9] h-10 rounded-xl"
          />
          <textarea
            data-testid="review-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            placeholder="How did it brew? (optional)"
            rows={2}
            className="w-full bg-[#F7F5F0] border border-[#E5E2D9] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#89937C]/30 resize-none"
          />
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="submit-review-btn"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 bg-[#2D372B] text-white text-sm font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
            >
              {submitting && <Loader2 size={13} className="animate-spin" />} Submit
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-full px-5 py-2 border border-[#E5E2D9] text-sm text-[#5C665A] hover:bg-[#F7F5F0] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {(data?.reviews || []).length > 0 && (
        <ul className="mt-4 space-y-3 border-t border-[#E5E2D9] pt-4">
          {data.reviews.slice(0, 3).map((r) => (
            <li key={r.id} data-testid="review-item">
              <div className="flex items-center gap-2">
                <Stars value={r.rating} size={11} />
                <span className="text-xs font-medium text-[#2D372B]">{r.name}</span>
              </div>
              {r.comment && <p className="text-xs text-[#5C665A] mt-1 leading-relaxed">{r.comment}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VariantDetailCard({ v, speciality }) {
  if (!v) return null;
  const rows = [
    ["Beans", v.beans],
    ["Composition", v.composition],
    ["Process", v.process],
    ["Grade", v.grade],
    ["Varietal", v.varietal],
    ["Best As / With", v.best],
    ["Tasting Notes", v.notes],
    ["Cupping Score", v.cupping],
  ].filter(([, val]) => val != null);
  return (
    <div
      data-testid="variant-detail-card"
      className="bg-[#2D372B] text-[#F7F5F0] rounded-2xl p-5 md:p-6 md:sticky md:top-24 shadow-xl"
    >
      <img
        src={`/coffee/${speciality}__${v.id}.jpg`}
        alt={v.name}
        data-testid="variant-photo"
        className="w-full h-40 object-cover rounded-xl mb-4"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <div className="flex items-center gap-2 mb-3">
        <Info size={15} className="text-[#E8C9B8]" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#C9CFC0] font-semibold">Tasting Card</span>
      </div>
      <div className="font-serif-display text-2xl mb-4">{v.name}</div>
      <ul className="space-y-2.5 text-sm">
        {rows.map(([label, val]) => (
          <li key={label} className="flex justify-between gap-4">
            <span className="text-[#A9B39D] shrink-0">{label}</span>
            <span className="text-right">{val}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function CoffeeCustomize() {
  const [speciality, setSpeciality] = useState("arabica");
  const [variant, setVariant] = useState(VARIANTS.arabica[0].id);
  const [roast, setRoast] = useState("medium");
  const [grind, setGrind] = useState("whole");
  const [qty, setQty] = useState("500");
  const [blendPct, setBlendPct] = useState(70);
  const [cart, setCart] = useState(getCart());
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { formatInr } = useCurrency();

  useEffect(() => {
    const sync = () => setCart(getCart());
    window.addEventListener("cart-updated", sync);
    return () => window.removeEventListener("cart-updated", sync);
  }, []);

  useEffect(() => {
    const p = routerLocation.state?.prefill;
    if (p) {
      if (p.speciality) selectSpeciality(p.speciality);
      if (p.roast) setRoast(p.roast);
      if (p.grind) setGrind(p.grind);
      if (p.size) setQty(String(p.size));
      toast.success("Your previous order is loaded — review and add to cart.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectSpeciality = (id) => {
    setSpeciality(id);
    setVariant(VARIANTS[id]?.[0]?.id ?? null);
    if (ROAST_LIMIT[id] && !ROAST_LIMIT[id].includes(roast)) setRoast("medium");
  };

  const variants = VARIANTS[speciality] || [];
  const activeVariant = variants.find((v) => v.id === variant) || null;
  const allowedRoasts = ROAST_LIMIT[speciality]
    ? ROASTS.filter((r) => ROAST_LIMIT[speciality].includes(r.id))
    : ROASTS;
  const effBlendPct = speciality === "custom_blend" ? blendPct : null;
  const itemPrice = useMemo(
    () => unitPriceInr(speciality, qty, effBlendPct),
    [speciality, qty, effBlendPct]
  );
  const isQuoteOnly = Boolean(QUANTITIES.find((q) => q.id === qty)?.quoteOnly);
  const cartSubtotal = cart.reduce((s, c) => s + c.unit_price * (c.bags || 1), 0);

  const handleAddToCart = () => {
    addToCart({
      speciality,
      variant: variants.length ? variant : null,
      roast,
      grind,
      size: qty,
      blend_arabica_pct: effBlendPct,
      unit_price: itemPrice,
    });
    toast.success("Added to cart", {
      description: `${specialityName(speciality)}${variant && variants.length ? ` · ${variantName(speciality, variant)}` : ""} — ${SIZE_LABELS[qty]}`,
    });
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/"
            data-testid="coffee-customize-back"
            className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] transition-colors"
          >
            <ArrowLeft size={16} /> Back to Coach Sanjay
          </Link>
          <button
            type="button"
            data-testid="go-to-cart-top"
            onClick={() => navigate("/coffee/checkout")}
            className="relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#2D372B] text-[#2D372B] text-sm font-medium hover:bg-[#2D372B] hover:text-white transition-colors"
          >
            <ShoppingCart size={16} />
            Cart
            {cart.length > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#C57B57] text-white text-[11px] flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </button>
        </div>

        <header className="mb-12 md:mb-16 max-w-2xl">
          <div className="overline mb-3">The Grounded Cup</div>
          <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.02]">
            Build your <span className="italic text-[#AD6A48]">bag.</span>
          </h1>
          <p className="mt-5 text-[#5C665A] leading-relaxed">
            Choose your coffee, roast, grind, and size. Add as many bags as you like to your cart —
            every bag is roasted to order, usually within 48 hours of brew day.
          </p>
        </header>

        <form onSubmit={(e) => e.preventDefault()} className="grid md:grid-cols-12 gap-8 md:gap-12" data-testid="coffee-customize-form">
          <div className="md:col-span-8 space-y-10">
            {/* Speciality */}
            <section>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-serif-display text-2xl md:text-3xl font-medium text-[#2D372B]">1. Speciality Coffee Selection</h2>
                <span className="overline">Choose one</span>
              </div>
              <RadioGroup value={speciality} onValueChange={selectSpeciality} className="grid sm:grid-cols-2 gap-4">
                {SPECIALITIES.map((s) => (
                  <label key={s.id} htmlFor={`speciality-${s.id}`} data-testid={`speciality-${s.id}`} className={`${cardCls(speciality === s.id)} p-5`}>
                    <div className="flex items-center gap-3 mb-2">
                      <RadioGroupItem id={`speciality-${s.id}`} value={s.id} />
                      <span className="font-serif-display text-xl text-[#2D372B]">{s.name}</span>
                    </div>
                    <p className="text-sm text-[#5C665A] leading-relaxed">{s.note}</p>
                    <p className="mt-1.5 text-xs text-[#AD6A48] font-medium">{formatInr(basePriceInr(s.id, 70) * 4)} / kg</p>
                  </label>
                ))}
              </RadioGroup>
            </section>

            {/* Variant picker + floating tasting card */}
            {variants.length > 0 && (
              <section data-testid="variant-section">
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-serif-display text-2xl md:text-3xl font-medium text-[#2D372B]">
                    Pick your {specialityName(speciality).replace(/ \(.*\)/, "")} lot
                  </h2>
                  <span className="overline">Choose one</span>
                </div>
                <div className="grid md:grid-cols-12 gap-5">
                  <div className="md:col-span-7">
                    <div className="flex flex-wrap gap-2.5">
                      {variants.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          data-testid={`variant-${v.id}`}
                          onClick={() => setVariant(v.id)}
                          className={`rounded-full px-4 py-2 text-sm border transition-all ${
                            variant === v.id
                              ? "bg-[#2D372B] text-[#F7F5F0] border-[#2D372B]"
                              : "bg-white text-[#2D372B] border-[#E5E2D9] hover:border-[#89937C]"
                          }`}
                        >
                          {v.name}
                          {v.cupping ? <span className={`ml-1.5 text-[11px] ${variant === v.id ? "text-[#E8C9B8]" : "text-[#AD6A48]"}`}>{v.cupping}</span> : null}
                        </button>
                      ))}
                    </div>
                    {ROAST_LIMIT[speciality] && (
                      <p className="mt-4 text-xs text-[#AD6A48]" data-testid="roast-limit-note">
                        These lots are roasted Light or Medium only — to preserve the delicate flavour infusions of the process.
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-5">
                    <VariantDetailCard v={activeVariant} speciality={speciality} />
                    <ReviewsBlock speciality={speciality} variant={variant} />
                  </div>
                </div>
              </section>
            )}

            {/* Custom blend slider */}
            {speciality === "custom_blend" && (
              <section data-testid="custom-blend-section">
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="font-serif-display text-2xl md:text-3xl font-medium text-[#2D372B]">Your blend ratio</h2>
                  <span className="overline">Slide to mix</span>
                </div>
                <div className="bg-white border border-[#E5E2D9] rounded-2xl p-6">
                  <div className="flex justify-between mb-4 text-sm font-medium">
                    <span className="text-[#2D372B]" data-testid="blend-arabica-label">{blendPct}% Arabica</span>
                    <span className="text-[#AD6A48]" data-testid="blend-robusta-label">{100 - blendPct}% Robusta</span>
                  </div>
                  <Slider
                    data-testid="blend-slider"
                    value={[blendPct]}
                    onValueChange={(v) => setBlendPct(v[0])}
                    min={10}
                    max={90}
                    step={5}
                  />
                  <p className="mt-4 text-sm text-[#5C665A]">
                    Smooth aromatic Arabica meets bold high-caffeine Robusta — roasted and blended to your exact ratio.
                    Price adjusts with your mix: <span className="text-[#2D372B] font-medium" data-testid="blend-price-per-kg">{formatInr(basePriceInr("custom_blend", blendPct) * 4)} / kg</span>.
                  </p>
                </div>
              </section>
            )}

            {/* AAA Arabica info */}
            {speciality === "aaa_arabica" && (
              <section data-testid="aaa-arabica-note">
                <div className="bg-[#2D372B] text-[#F7F5F0] rounded-2xl p-6">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9CFC0] font-semibold mb-2">Premium Grade</div>
                  <div className="font-serif-display text-2xl mb-2">AAA Arabica</div>
                  <p className="text-sm text-[#C9CFC0] leading-relaxed">
                    Our top screen-size AAA grade Arabica — the largest, densest beans hand-sorted from premium
                    estate lots. Exceptionally clean cup, refined sweetness and remarkable aroma. {formatInr(2200)} / kg.
                  </p>
                </div>
              </section>
            )}

            {/* Roast */}
            <section>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-serif-display text-2xl md:text-3xl font-medium text-[#2D372B]">2. Roast</h2>
                <span className="overline">Choose one</span>
              </div>
              <RadioGroup value={roast} onValueChange={setRoast} className="grid sm:grid-cols-3 gap-4">
                {allowedRoasts.map((r) => (
                  <label key={r.id} htmlFor={`roast-${r.id}`} data-testid={`roast-${r.id}`} className={`${cardCls(roast === r.id)} p-5`}>
                    <div className="flex items-center gap-3 mb-2">
                      <RadioGroupItem id={`roast-${r.id}`} value={r.id} />
                      <span className="font-serif-display text-xl text-[#2D372B]">{r.name}</span>
                    </div>
                    <p className="text-sm text-[#5C665A] leading-relaxed">{r.note}</p>
                  </label>
                ))}
              </RadioGroup>
            </section>

            {/* Grind */}
            <section>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-serif-display text-2xl md:text-3xl font-medium text-[#2D372B]">3. Grind</h2>
                <span className="overline">Choose one</span>
              </div>
              <RadioGroup value={grind} onValueChange={setGrind} className="grid sm:grid-cols-2 gap-3">
                {GRINDS.map((g) => (
                  <label key={g.id} htmlFor={`grind-${g.id}`} data-testid={`grind-${g.id}`} className={`${cardCls(grind === g.id)} px-5 py-4 flex items-center gap-3`}>
                    <RadioGroupItem id={`grind-${g.id}`} value={g.id} />
                    <span className="text-[#2D372B]">{g.name}</span>
                  </label>
                ))}
              </RadioGroup>
            </section>

            {/* Quantity */}
            <section>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-serif-display text-2xl md:text-3xl font-medium text-[#2D372B]">4. Quantity</h2>
                <span className="overline">Choose one</span>
              </div>
              <RadioGroup value={qty} onValueChange={setQty} className="grid sm:grid-cols-3 gap-3">
                {QUANTITIES.map((q) => (
                  <label key={q.id} htmlFor={`qty-${q.id}`} data-testid={`qty-${q.id}`} className={`${cardCls(qty === q.id)} px-5 py-4 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem id={`qty-${q.id}`} value={q.id} />
                      <span className="text-[#2D372B]">{q.label}</span>
                    </div>
                    <span className="font-serif-display text-lg text-[#2D372B]">
                      {q.quoteOnly ? (
                        <span className="text-sm text-[#AD6A48] italic">Quote on request</span>
                      ) : (
                        formatInr(unitPriceInr(speciality, q.id, effBlendPct))
                      )}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </section>
          </div>

          {/* Aside: current bag + cart */}
          <aside className="md:col-span-4">
            <div className="md:sticky md:top-24 space-y-5">
              <div className="bg-white border border-[#E5E2D9] rounded-3xl p-6 md:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Coffee size={18} className="text-[#AD6A48]" />
                  <span className="overline">Your bag</span>
                </div>
                <ul className="space-y-3 text-sm text-[#5C665A]">
                  <li className="flex justify-between">
                    <span>Speciality</span>
                    <span className="text-[#2D372B] font-medium text-right">{specialityName(speciality)}</span>
                  </li>
                  {variants.length > 0 && (
                    <li className="flex justify-between">
                      <span>Lot</span>
                      <span className="text-[#2D372B] font-medium text-right" data-testid="summary-variant">{variantName(speciality, variant)}</span>
                    </li>
                  )}
                  {speciality === "custom_blend" && (
                    <li className="flex justify-between">
                      <span>Blend</span>
                      <span className="text-[#2D372B] font-medium">{blendPct}% Arabica · {100 - blendPct}% Robusta</span>
                    </li>
                  )}
                  <li className="flex justify-between">
                    <span>Roast</span>
                    <span className="text-[#2D372B] font-medium">{ROASTS.find((r) => r.id === roast)?.name}</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Grind</span>
                    <span className="text-[#2D372B] font-medium">{GRINDS.find((g) => g.id === grind)?.name}</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Quantity</span>
                    <span className="text-[#2D372B] font-medium">{SIZE_LABELS[qty]}</span>
                  </li>
                </ul>
                <div className="mt-5 pt-5 border-t border-[#E5E2D9] flex items-baseline justify-between">
                  <span className="overline">Item price</span>
                  <span className="font-serif-display text-2xl text-[#2D372B]" data-testid="summary-item-price">
                    {isQuoteOnly ? <span className="text-lg italic text-[#AD6A48]">On request</span> : formatInr(itemPrice)}
                  </span>
                </div>
                {isQuoteOnly ? (
                  <a
                    href="/#contact"
                    data-testid="coffee-quote-btn"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors"
                  >
                    Request a quote
                  </a>
                ) : (
                  <button
                    type="button"
                    data-testid="add-to-cart-btn"
                    onClick={handleAddToCart}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors"
                  >
                    <ShoppingBag size={16} /> Add to Cart
                  </button>
                )}
                <p className="mt-3 text-xs text-[#5C665A] text-center">
                  Shipping, GST (India), vouchers &amp; loyalty points are applied at checkout.
                </p>
              </div>

              {cart.length > 0 && (
                <div data-testid="mini-cart" className="bg-[#2D372B] rounded-3xl p-6 text-[#F7F5F0]">
                  <div className="flex items-center gap-2 mb-4">
                    <ShoppingCart size={16} className="text-[#E8C9B8]" />
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#C9CFC0] font-semibold">
                      Cart — {cart.length} item{cart.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <ul className="space-y-2.5 text-sm">
                    {cart.map((c) => (
                      <li key={c.line_id} className="flex items-start justify-between gap-2" data-testid="mini-cart-item">
                        <span className="text-[#C9CFC0] leading-snug">
                          {c.bags}× {SIZE_LABELS[c.size]} {specialityName(c.speciality)}
                          {c.variant ? ` · ${variantName(c.speciality, c.variant)}` : ""}
                          {c.blend_arabica_pct != null ? ` · ${c.blend_arabica_pct}% Arabica` : ""}
                        </span>
                        <button
                          type="button"
                          data-testid="mini-cart-remove"
                          onClick={() => removeFromCart(c.line_id)}
                          className="p-1 text-[#A9B39D] hover:text-white transition-colors shrink-0"
                          aria-label="Remove item"
                        >
                          <Trash2 size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-white/15 flex justify-between text-sm">
                    <span className="text-[#A9B39D]">Items subtotal</span>
                    <span className="font-medium" data-testid="mini-cart-subtotal">{formatInr(cartSubtotal)}</span>
                  </div>
                  <button
                    type="button"
                    data-testid="proceed-to-checkout-btn"
                    onClick={() => navigate("/coffee/checkout")}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              )}
            </div>
          </aside>
        </form>

        <div data-testid="supplier-teaser" className="mt-14 bg-[#2D372B] rounded-3xl p-8 sm:p-10 text-center">
          <div className="overline !text-[#A9B39D]">For Growers &amp; Exporters</div>
          <h2 className="font-serif-display text-3xl text-[#F7F5F0] mt-2">Grow or supply exceptional coffee?</h2>
          <p className="text-sm text-[#C9CFC0] mt-3 max-w-xl mx-auto">
            We partner with Indian and international estates, farmers and exporters to source our beans.
            Tell us about your origin, elevation and lots — let's talk.
          </p>
          <Link
            to="/suppliers"
            data-testid="supplier-teaser-link"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 bg-[#F7F5F0] text-[#2D372B] font-medium hover:bg-[#C9CFC0] transition-colors"
          >
            Become a supplier
          </Link>
        </div>
      </div>
    </div>
  );
}
